import { Flag, Calendar, Clock, User, CircleDot, Layers, Tag as TagIcon } from 'lucide-react';
import { FieldValueCell, resolveFieldType, FIELD_REGISTRY } from '@/features/custom-fields/public';
import type { ProjectField } from '@/features/custom-fields/public';
import type { ColumnDef } from './types';
export type { RowContext, ColumnDef } from './types';
import { StatusPicker } from '../TaskDialog/StatusPicker';
import { AssigneePicker } from '../TaskDialog/AssigneePicker';
import { PriorityPicker } from './PriorityPicker';
import { TaskDatePicker } from './TaskDatePicker';
import { EstimatedPicker } from '../EstimatedPicker';
import { GroupPicker } from '../GroupPicker';
import { TagPicker } from '../TagPicker';
import { useTags } from '@/features/tags/public';
import { resolveTaskTags, useAttachTag, useDetachTag } from '../../hooks/useTaskTags';
import type { Tag } from '@/features/tags/public';

interface TagsColumnCellProps {
  taskId: string;
  projectId: string;
  tagIds?: string[];
}

function TagsColumnCell({ taskId, projectId, tagIds }: TagsColumnCellProps) {
  const { data: projectTagsData } = useTags(projectId);
  const allProjectTags = (projectTagsData?.items ?? []) as Tag[];
  const selectedTags = resolveTaskTags(tagIds, allProjectTags);
  const attachTag = useAttachTag(taskId);
  const detachTag = useDetachTag(taskId);

  function handleChange(newTags: Tag[]) {
    const currentIds = new Set(selectedTags.map((t) => t.id));
    const newIds = new Set(newTags.map((t) => t.id));
    newTags.filter((t) => !currentIds.has(t.id)).forEach((tag) => attachTag.mutate(tag));
    selectedTags.filter((t) => !newIds.has(t.id)).forEach((t) => detachTag.mutate(t.id ?? ''));
  }

  return (
    <TagPicker
      projectId={projectId}
      selectedTags={selectedTags}
      onChange={handleChange}
      variant="compact"
    />
  );
}

export const BUILT_IN_COLUMN_DEFS: ColumnDef[] = [
  {
    id: 'status',
    labelKey: 'listView.columns.status',
    icon: CircleDot,
    iconColorClass: 'text-muted-foreground',
    defaultWidthPx: 128,
    breakpoint: 'md:flex',
    sortable: true,
    renderCell: (task, ctx) => (
      <StatusPicker
        boardId={task.status.boardId ?? ctx.boardId ?? ''}
        currentStatus={task.status}
        onSelect={(statusId) => ctx.onStatusChange?.(task.id, statusId)}
      />
    ),
  },
  {
    id: 'priority',
    labelKey: 'listView.columns.priority',
    icon: Flag,
    iconColorClass: 'text-muted-foreground',
    defaultWidthPx: 96,
    breakpoint: 'md:flex',
    sortable: true,
    renderCell: (task, ctx) => (
      <PriorityPicker
        priority={task.priority}
        onSelect={(p) => ctx.onPriorityChange?.(task.id, p)}
      />
    ),
  },
  {
    id: 'assignee',
    labelKey: 'listView.columns.assignee',
    icon: User,
    iconColorClass: 'text-muted-foreground',
    defaultWidthPx: 128,
    breakpoint: 'md:flex',
    sortable: false,
    renderCell: (task, ctx) => (
      <AssigneePicker
        projectId={task.projectId ?? ''}
        assignee={task.assignee ?? null}
        onSelect={(assigneeId) => ctx.onAssigneeChange?.(task.id, assigneeId)}
      />
    ),
  },
  {
    id: 'dueDate',
    labelKey: 'listView.columns.dueDate',
    icon: Calendar,
    iconColorClass: 'text-muted-foreground',
    defaultWidthPx: 144,
    breakpoint: 'lg:flex',
    sortable: true,
    renderCell: (task, ctx) => (
      <TaskDatePicker
        startDate={task.startDate}
        endDate={task.dueDate}
        statusType={task.status.type}
        dateFormat={ctx.dateFormat}
        timeFormat={ctx.timeFormat}
        onSave={(start, end) => ctx.onDateChange?.(task.id, start, end)}
      />
    ),
  },
  {
    id: 'estimated',
    labelKey: 'listView.columns.estimated',
    icon: Clock,
    iconColorClass: 'text-muted-foreground',
    defaultWidthPx: 96,
    breakpoint: 'lg:flex',
    sortable: false,
    renderCell: (task, ctx) => (
      <EstimatedPicker
        value={task.estimatedMinutes ?? null}
        startDate={task.startDate}
        dueDate={task.dueDate}
        onSave={(minutes) => ctx.onEstimatedChange?.(task.id, minutes)}
      />
    ),
  },
  {
    id: 'tags',
    labelKey: 'listView.columns.tags',
    icon: TagIcon,
    iconColorClass: 'text-muted-foreground',
    defaultWidthPx: 192,
    breakpoint: 'lg:flex',
    sortable: false,
    renderCell: (task) => (
      <TagsColumnCell taskId={task.id} projectId={task.projectId ?? ''} tagIds={task.tagIds} />
    ),
  },
  {
    id: 'group',
    labelKey: 'listView.columns.group',
    icon: Layers,
    iconColorClass: 'text-muted-foreground',
    defaultWidthPx: 128,
    breakpoint: 'md:flex',
    sortable: false,
    renderCell: (task, ctx) => (
      <GroupPicker
        boardId={ctx.boardId ?? task.status.boardId ?? ''}
        projectId={task.projectId ?? ''}
        currentGroupId={task.groupId}
        onSelect={(groupId) => ctx.onGroupChange?.(task.id, groupId)}
      />
    ),
  },
];

export const BUILT_IN_COLUMN_MAP: Readonly<Record<string, ColumnDef>> = Object.fromEntries(
  BUILT_IN_COLUMN_DEFS.map((c) => [c.id, c]),
);

/**
 * Converts a `ProjectField` definition into a `ColumnDef` for use in the task list.
 * The resulting `renderCell` renders a `FieldValueCell` for the given task and field.
 */
export function makeCustomFieldColumn(field: ProjectField): ColumnDef {
  const fieldType = resolveFieldType(field.fieldType);
  const entry = fieldType != null ? FIELD_REGISTRY[fieldType] : null;
  const iconColorClass = 'text-muted-foreground';
  return {
    id: field.id ?? '',
    label: field.name ?? '',
    icon: entry?.icon ?? Layers,
    iconColorClass,
    defaultWidthPx: 128,
    breakpoint: 'md:flex',
    sortable: false,
    renderCell: (task, ctx) => (
      <FieldValueCell
        fieldId={field.id ?? ''}
        taskId={task.id}
        field={field}
        mode="list"
        // Always a defined array (never undefined) here: the list view owns
        // fetching via useTaskFieldValuesBatch, so this cell must never fall
        // back to its own per-task query, even while the batch is loading.
        preloadedValues={ctx.customFieldValuesByTask?.[task.id] ?? []}
      />
    ),
  };
}

/** Resolves a column's effective width — the board's saved override, or the registry default. */
export function getColumnWidthPx(
  columnId: string,
  def: { defaultWidthPx: number } | undefined,
  columnWidths: Record<string, number>,
): number {
  return columnWidths[columnId] ?? def?.defaultWidthPx ?? 128;
}

/**
 * Moves `sourceCol` next to `targetCol` within `columns`, on the side given by `edge` — dropping
 * on the left half of a column places the source right before it, the right half right after.
 * Pure so the reorder math is unit-testable without simulating a real drag-and-drop gesture.
 */
export function reorderColumns(
  columns: string[],
  sourceCol: string,
  targetCol: string,
  edge: 'left' | 'right',
): string[] {
  if (sourceCol === targetCol) return columns;
  const withoutSource = columns.filter((c) => c !== sourceCol);
  const targetIndex = withoutSource.indexOf(targetCol);
  if (targetIndex === -1) return columns;
  const insertIndex = edge === 'right' ? targetIndex + 1 : targetIndex;
  withoutSource.splice(insertIndex, 0, sourceCol);
  return withoutSource;
}
